import { convertDateTimesToString } from "utils/apiHelpers";
import { licensesApi, plansApi, subscriptionRateCardsApi, subscriptionsApi } from "client/api-v2";
import type { LicenseEmailDefaultRecipientsDto, LicenseEmailDto } from "client/api-v2";
import type {
  Subscription,
  SubscriptionWithSerial,
  CreateSubscriptionDto,
  UpdateSubscriptionRequestDto,
  CancelSubscriptionRequestDto,
  Plan,
  SubscriptionStatus,
  LifecycleType,
  ComplimentaryType,
  PlanListResponse,
  ProductPlatform,
  Tier,
  EntityHistoryReferenceDto,
  ServerLicenseDto,
  CloudSubscriptionDto,
  EntitlementConfiguration,
  AutoRenewPeriod,
  ExtendSubscriptionTermRequestDto,
  SubscriptionWithSerialListResponse,
  RateCardAssignmentHistoryEntry,
} from "client/api-v2";
import type { UpdateComplimentaryAutoRenewalRequest, GetTrialEntitlementsResponse } from "client/api-v2";
import axiosClient from "client/axios-client";

export type TrialEntitlementsResponse = GetTrialEntitlementsResponse;

export interface SubscriptionHistorySnapshot {
  subscription: Record<string, unknown> | null;
  licenseEntity: Record<string, unknown> | null;
  licenseEntityFetchFailed: boolean;
}

export class SubscriptionService {
  static async getSubscriptions(params?: {
    status?: SubscriptionStatus;
    lifecycleType?: LifecycleType;
    complimentaryType?: ComplimentaryType;
    tier?: Tier;
    page?: number;
    pageSize?: number;
    serial?: string;
    ownerEmail?: string;
    termStartAfter?: string | Date;
    termStartBefore?: string | Date;
    termEndAfter?: string | Date;
    termEndBefore?: string | Date;
    zuoraSubscriptionKey?: string;
    friendlyName?: string;
    dnsPrefix?: string;
    sortFieldName?: string;
    sortDirection?: "Asc" | "Desc";
  }): Promise<SubscriptionWithSerialListResponse> {
    try {
      const result = await getSubscriptions(params || {}, new AbortController().signal);
      return {
        items: result.items,
        totalItems: result.totalItems,
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        totalPages: result.totalItems,
        hasNextPage: result.totalItems > (params?.page || 1) * (params?.pageSize || 20),
        hasPreviousPage: (params?.page || 1) > 1,
      };
    } catch (error) {
      console.error("Failed to fetch subscriptions:", error);
      throw new Error(`Failed to fetch subscriptions: ${error}`);
    }
  }

  static async getSubscription(id: string): Promise<Subscription> {
    try {
      return await getSubscriptionById(id);
    } catch (error) {
      console.error(`Failed to fetch subscription ${id}:`, error);
      throw new Error(`Failed to fetch subscription: ${error}`);
    }
  }

  static async getHistorySnapshot(id: string, asOf: string): Promise<SubscriptionHistorySnapshot> {
    const response = await axiosClient.get<SubscriptionHistorySnapshot>(`/v2/subscriptions/${id}/history-snapshot`, {
      params: { asOf },
    });
    return response.data;
  }

  static async createSubscription(request: CreateSubscriptionDto): Promise<Subscription> {
    try {
      const { data } = await subscriptionsApi.postV2Subscriptions({ body: request });
      if (!data.subscription) {
        throw new Error("Invalid response: subscription data not found");
      }
      return data.subscription;
    } catch (error) {
      console.error("Failed to create subscription:", error);
      throw error;
    }
  }

  static async updateSubscription(id: string, request: UpdateSubscriptionRequestDto): Promise<Subscription> {
    try {
      const { data } = await subscriptionsApi.patchV2SubscriptionsById({ path: { id }, body: request });
      return data;
    } catch (error) {
      console.error(`Failed to update subscription ${id}:`, error);
      throw new Error(`Failed to update subscription: ${error}`);
    }
  }

  static async cancelSubscription(id: string, reason: string): Promise<Subscription> {
    try {
      const request: CancelSubscriptionRequestDto = { reason };
      const { data } = await subscriptionsApi.postV2SubscriptionsByIdCancel({ path: { id }, body: request });
      return data;
    } catch (error) {
      console.error(`Failed to cancel subscription ${id}:`, error);
      throw new Error(`Failed to cancel subscription: ${error}`);
    }
  }

  static async reactivateSubscription(
    id: string,
    newTermStart: string,
    newTermEnd: string | null
  ): Promise<Subscription> {
    try {
      const request = { newTermStart, newTermEnd };
      const { data } = await subscriptionsApi.postV2SubscriptionsByIdReactivate({ path: { id }, body: request });
      return data;
    } catch (error) {
      console.error(`Failed to reactivate subscription ${id}:`, error);
      // Re-throw the original error to preserve axios error information
      throw error;
    }
  }

  static async updateEntitlementOverrides(id: string, overrides: EntitlementConfiguration): Promise<Subscription> {
    try {
      const request = { entitlementOverrides: overrides };
      const { data } = await subscriptionsApi.patchV2SubscriptionsByIdEntitlementOverrides({
        path: { id },
        body: request,
      });
      return data;
    } catch (error) {
      console.error(`Failed to update entitlement overrides for subscription ${id}:`, error);
      throw error;
    }
  }

  static async updateAutoRenewal(id: string, autoRenewPeriod: AutoRenewPeriod | null): Promise<void> {
    try {
      const request: UpdateComplimentaryAutoRenewalRequest = { id, autoRenewPeriod: autoRenewPeriod ?? undefined };
      await subscriptionsApi.patchV2SubscriptionsByIdAutoRenewal({ path: { id }, query: { request } });
    } catch (error) {
      console.error(`Failed to update auto-renewal for subscription ${id}:`, error);
      throw error;
    }
  }

  static async updateZuoraSubscriptionKey(id: string, zuoraSubscriptionKey: string | null): Promise<Subscription> {
    try {
      const request = { zuoraSubscriptionKey };
      const { data } = await subscriptionsApi.patchV2SubscriptionsByIdZuoraSubscriptionKey({
        path: { id },
        body: request,
      });
      return data;
    } catch (error) {
      console.error(`Failed to update Zuora subscription key for subscription ${id}:`, error);
      throw error;
    }
  }

  static async updateSelfServiceEnabled(id: string, selfServiceEnabled: boolean): Promise<Subscription> {
    try {
      const { data } = await subscriptionsApi.patchV2SubscriptionsByIdSelfServiceEnabled({
        path: { id },
        body: { selfServiceEnabled },
      });
      return data;
    } catch (error) {
      console.error(`Failed to update self-service for subscription ${id}:`, error);
      throw error;
    }
  }

  static async changeSubscriptionPlan(
    id: string,
    planId: string,
    clearEntitlementOverrides = false
  ): Promise<Subscription> {
    try {
      const request = { planId, clearEntitlementOverrides };
      const { data } = await subscriptionsApi.patchV2SubscriptionsByIdPlan({ path: { id }, body: request });
      return data;
    } catch (error) {
      console.error(`Failed to change plan for subscription ${id}:`, error);
      throw error;
    }
  }

  static async extendSubscriptionTerm(id: string, request: ExtendSubscriptionTermRequestDto): Promise<Subscription> {
    try {
      const { data } = await subscriptionsApi.postV2SubscriptionsByIdExtendTerm({ path: { id }, body: request });
      return data;
    } catch (error) {
      console.error(`Failed to extend subscription term ${id}:`, error);
      throw error;
    }
  }

  static async deleteSubscription(id: string): Promise<void> {
    // Delete operation not available in current API
    console.warn(`Delete subscription operation not available for subscription ${id}`);
    throw new Error("Delete subscription operation is not available");
  }

  static async getTrialEntitlements(): Promise<TrialEntitlementsResponse> {
    try {
      const { data } = await subscriptionsApi.getV2SubscriptionsTrialEntitlements();
      return data;
    } catch (error) {
      console.error("Failed to fetch trial entitlements:", error);
      throw error;
    }
  }

  static async getSubscriptionHistory(id: string): Promise<EntityHistoryReferenceDto[]> {
    try {
      const { data } = await subscriptionsApi.getV2SubscriptionsByIdHistory({ path: { id } });
      return data;
    } catch (error) {
      console.error(`Failed to fetch subscription history ${id}:`, error);
      throw new Error(`Failed to fetch subscription history: ${error}`);
    }
  }

  static async searchSubscriptions(searchTerm: string, limit: number = 50): Promise<SubscriptionWithSerial[]> {
    try {
      const response = await this.getSubscriptions({
        friendlyName: searchTerm,
        pageSize: limit,
        page: 1,
      });
      return response.items || [];
    } catch (error) {
      console.error("Failed to search subscriptions:", error);
      throw new Error(`Failed to search subscriptions: ${error}`);
    }
  }
}

export class PlanService {
  static async getPlans(params?: {
    platform?: ProductPlatform;
    tier?: Tier;
    availableDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PlanListResponse> {
    try {
      const { data } = await plansApi.getV2Plans({
        query: {
          platform: params?.platform,
          tier: params?.tier,
          availableDate: params?.availableDate,
          page: params?.page || 1,
          pageSize: params?.pageSize || 50,
        },
      });
      return data;
    } catch (error) {
      console.error("Failed to fetch plans:", error);
      throw new Error(`Failed to fetch plans: ${error}`);
    }
  }

  static async getPlan(id: string): Promise<Plan> {
    try {
      const plan = await getPlanById(id);
      if (!plan) {
        throw new Error(`Plan with ID ${id} not found`);
      }
      return plan;
    } catch (error) {
      console.error(`Failed to fetch plan ${id}:`, error);
      throw new Error(`Failed to fetch plan: ${error}`);
    }
  }
}

function toDateString(value: string | Date | undefined): string | undefined {
  const converted = convertDateTimesToString(value);
  return typeof converted === "string" ? converted : undefined;
}

export async function getSubscriptions(
  params: {
    status?: SubscriptionStatus;
    lifecycleType?: LifecycleType;
    complimentaryType?: ComplimentaryType;
    tier?: Tier;
    page?: number;
    pageSize?: number;
    serial?: string;
    ownerEmail?: string;
    termStartAfter?: string | Date;
    termStartBefore?: string | Date;
    termEndAfter?: string | Date;
    termEndBefore?: string | Date;
    zuoraSubscriptionKey?: string;
    friendlyName?: string;
    dnsPrefix?: string;
    rateCardCode?: string;
    sortFieldName?: string;
    sortDirection?: "Asc" | "Desc";
  },
  signal: AbortSignal
): Promise<{ items: SubscriptionWithSerial[]; totalItems: number }> {
  const { data } = await subscriptionsApi.getV2Subscriptions({
    query: {
      status: params.status,
      lifecycleType: params.lifecycleType,
      complimentaryType: params.complimentaryType,
      tier: params.tier,
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      serial: params.serial,
      dnsPrefix: params.dnsPrefix,
      ownerEmail: params.ownerEmail,
      termStartAfter: toDateString(params.termStartAfter),
      termStartBefore: toDateString(params.termStartBefore),
      termEndAfter: toDateString(params.termEndAfter),
      termEndBefore: toDateString(params.termEndBefore),
      zuoraSubscriptionKey: params.zuoraSubscriptionKey,
      friendlyName: params.friendlyName,
      rateCardCode: params.rateCardCode,
      sortFieldName: params.sortFieldName,
      sortDirection: params.sortDirection,
    },
    signal,
  });

  return {
    items: data.items || [],
    totalItems: data.totalItems || 0,
  };
}

export async function getSubscriptionById(id: string, signal?: AbortSignal): Promise<Subscription> {
  if (!id || id === "UNKNOWN_SUBSCRIPTION") {
    throw new Error("Invalid subscription ID");
  }
  const { data } = await subscriptionsApi.getV2SubscriptionsById({ path: { id }, signal });
  return data;
}

export async function getPlanById(id: string, signal?: AbortSignal): Promise<Plan | null> {
  if (!id) {
    return null;
  }
  const { data } = await plansApi.getV2PlansById({ path: { id }, signal });
  return data;
}

export async function createSubscription(request: CreateSubscriptionDto): Promise<Subscription> {
  const { data } = await subscriptionsApi.postV2Subscriptions({ body: request });
  if (!data.subscription) {
    throw new Error("Invalid response: subscription data not found");
  }
  return data.subscription;
}

export async function getServerLicenseBySubscriptionId(subscriptionId: string): Promise<ServerLicenseDto> {
  try {
    const { data } = await licensesApi.getV2LicensesOctopusServerSubscriptionBySubscriptionId({
      path: { subscriptionId },
    });
    return data;
  } catch (error) {
    console.error(`Failed to fetch server license for subscription ${subscriptionId}:`, error);
    throw new Error(`Failed to fetch server license: ${error}`);
  }
}

export async function getCloudSubscriptionBySubscriptionId(subscriptionId: string): Promise<CloudSubscriptionDto> {
  try {
    const { data } = await licensesApi.getV2LicensesOctopusCloudSubscriptionBySubscriptionId({
      path: { subscriptionId },
    });
    return data;
  } catch (error) {
    console.error(`Failed to fetch cloud subscription for subscription ${subscriptionId}:`, error);
    throw new Error(`Failed to fetch cloud subscription: ${error}`);
  }
}

export default SubscriptionService;

export type { LicenseEmailDefaultRecipientsDto };

export async function getLicenseEmailDefaultRecipients(
  subscriptionId: string
): Promise<LicenseEmailDefaultRecipientsDto> {
  const { data } = await subscriptionsApi.getV2SubscriptionsByIdLicenseEmailsDefaultRecipients({
    path: { id: subscriptionId },
  });
  return data;
}

export async function sendLicenseEmail(
  subscriptionId: string,
  recipients: { to: string[]; cc: string[]; bcc: string[] }
): Promise<void> {
  await subscriptionsApi.postV2SubscriptionsByIdLicenseEmails({
    path: { id: subscriptionId },
    body: {
      to: recipients.to,
      cc: recipients.cc,
      bcc: recipients.bcc,
    },
  });
}

export async function getLicenseEmailHistory(subscriptionId: string): Promise<LicenseEmailDto[]> {
  const { data } = await subscriptionsApi.getV2SubscriptionsByIdLicenseEmails({ path: { id: subscriptionId } });
  return data;
}

export async function getRateCardAssignmentHistory(
  subscriptionId: string | undefined
): Promise<RateCardAssignmentHistoryEntry[]> {
  if (!subscriptionId) return [];

  const { data } = await subscriptionRateCardsApi.getV2SubscriptionsBySubscriptionIdRateCardAssignmentsHistory({
    path: { subscriptionId },
  });
  return data;
}
